import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { ValuesMap } from '@/common/types';
import { API_URL } from '@/dtos';
import {
  CONTACT_ORDER_IDS,
  IPermission,
  IPermissionGroup,
  IRawPermissionResponse,
  SUPER_USER_ROLE_ID,
} from '@/modules/role/role.types';

const QUERY_KEY = 'PERMISSIONS_LIST_QUERY_KEY';

interface Params {
  roleId?: string;
  searchString?: string;
  isRoleEditAllowed?: boolean;
}

export const ALWAYS_ON_IDS = [1, 34, 39, 54];
export const ALWAYS_ON_IDS_SUPER_USER = [...ALWAYS_ON_IDS, 72, 73];
export const GLOBAL_PERMISSIONS = [72, 73];
interface Response {
  order: number;
  permissions: IRawPermissionResponse[];
}

interface PermissionOrder {
  id: string;
  name: string;
  order: number;
}

const usePermissionsListQuery = ({ searchString, isRoleEditAllowed = false, roleId }: Params) => {
  const { isError, isSuccess, data, isLoading } = useQuery([QUERY_KEY], () =>
    // TODO: the type is missing
    api.get<ValuesMap<Response>>(API_URL.PERMISSIONS_ALL)
  );

  const currentData = useMemo<IPermission[]>(() => {
    const currentData = data?.data || {};

    const currentMap = _.sortBy(Object.values(currentData), 'order').map((item) => item.permissions);

    const currentList = _.flatten(currentMap) || [];
    const filteredList = currentList.filter((item) =>
      isRoleEditAllowed || roleId === SUPER_USER_ROLE_ID ? true : !GLOBAL_PERMISSIONS.includes(item.id)
    );
    const newList = filteredList.map((item) => ({
      key: `${item.id}`,
      id: `${item.id}`,
      isAlwaysOn:
        roleId === SUPER_USER_ROLE_ID
          ? ALWAYS_ON_IDS_SUPER_USER.includes(item.id)
          : ALWAYS_ON_IDS.includes(item.id),
      name: item.display_name || item.name,
      description: item.description,
      value: item.display_name,
      groupName: item.group_name,
      group: item.group_id,
    }));

    return newList;
  }, [data?.data, isRoleEditAllowed, roleId]);

  const filteredData = useMemo(() => {
    if (!searchString) {
      return currentData;
    }
    return currentData.filter((data) =>
      data.name?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
    );
  }, [currentData, searchString]);
  const permissionsMap = useMemo(() => {
    const currentMap: ValuesMap<IPermission> = {};
    currentData.forEach((option) => {
      currentMap[option.key as string] = option;
    });
    return currentMap;
  }, [currentData]);
  const groupedByOptions = useMemo(() => {
    const optionsMap: ValuesMap<IPermission[]> = {};
    const list = filteredData;
    list.forEach((item) => {
      if (item.group) {
        if (optionsMap[item.group]) {
          optionsMap[item.group] = [...optionsMap[item.group], item];
        } else {
          optionsMap[item.group] = [item];
        }
      }
    });

    return optionsMap;
  }, [filteredData]);
  const groups = useMemo(() => {
    const currentData = data?.data || {};
    const orderMap: ValuesMap<PermissionOrder> = {};
    Object.keys(currentData).forEach((key) => {
      const currentItem = currentData[key];
      const firstItem = _.first(currentItem.permissions);

      if (firstItem && firstItem.group_id) {
        orderMap[firstItem.group_id] = {
          id: `${firstItem.group_id}`,
          name: firstItem.group_name,
          order: currentItem.order,
        };
      }
      return {
        key: key,
        order: currentData[key].order,
      };
    });

    return Object.values(orderMap);
  }, [data?.data]);

  const keysOrder = useMemo(() => {
    const sortedList = _.sortBy(groups, 'order').map((item) => item.id);
    return sortedList;
  }, [groups]);
  const formattedGroups: IPermissionGroup[] = useMemo(() => {
    const keys = keysOrder;
    return keys.map((key) => {
      const currentOptions = groupedByOptions[key] || [];
      let isMatchFound = false;
      const reorderedArray = currentOptions.reduce((result: IPermission[], currentItem) => {
        if (CONTACT_ORDER_IDS.includes(currentItem.key as string)) {
          if (isMatchFound) {
            return result;
          }
          isMatchFound = true;
          const foundItems = currentOptions.filter((sortedItem) =>
            CONTACT_ORDER_IDS.includes(sortedItem.key as string)
          );

          return [...result, ...foundItems];
        }
        return [...result, currentItem];
      }, []);
      const foundGroup = groups.find((group) => group.id === key);
      return {
        id: key,
        name: foundGroup?.name || key,
        options: reorderedArray,
      };
    });
  }, [groupedByOptions, groups, keysOrder]);
  return {
    permissionsMap,
    options: currentData,
    groups: formattedGroups,
    isLoading,
    isSuccess,
    isError,
  };
};
export default usePermissionsListQuery;
